import { useRef } from 'react';

import { Typography } from '@remarkable/ark-web';
import { getRouteApi } from '@tanstack/react-router';
import {
  functionalUpdate,
  getCoreRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useDebouncedCallback } from 'use-debounce';

import { Input, SomethingWentWrong, Table } from 'src/components';

import { useQuery } from '@tanstack/react-query';
import { queryEntepriseOrgSettings } from 'src/api/queries/enterpriseAdmin';
import { useAccessTokenClaims } from 'src/api/queries/user';
import { userHelpers } from 'src/utils/userHelpers';
import { AddDomains } from './AddDomains';
import { domainsTableColumns } from './domainsTableColumns';

const routeApi = getRouteApi('/_auth/enterprise/_with-org/_layout/domains');

export const DomainsTable = () => {
  const settings = useQuery(queryEntepriseOrgSettings());
  const accessTokenClaims = useAccessTokenClaims();

  const search = routeApi.useSearch();
  const navigate = routeApi.useNavigate();

  const debouncedGlobalFilter = useDebouncedCallback((value: string) => {
    void navigate({
      to: '.',
      search: { ...search, filter: value.trim() || undefined },
      replace: true,
    });
  }, 100);
  const tableRef = useRef(null);

  const table = useReactTable({
    data: settings.data?.domains || [],
    columns: domainsTableColumns,
    initialState: {
      pagination: {
        pageSize: 25,
      },
    },
    state: {
      globalFilter: search.filter?.trim() || undefined,
      columnVisibility: {
        edit: userHelpers.hasPermissionUpdateOrgSettings(
          accessTokenClaims.data
        ),
      },
    },
    onGlobalFilterChange: (updater: string | undefined) => {
      void navigate({
        to: '.',
        search: (prev) => ({
          ...prev,
          filter: functionalUpdate(updater, prev.filter)?.trim() || undefined,
        }),
        replace: true,
      });
    },
    meta: {
      fillRowsToMatchPageSize: false,
    },
    getRowId: (_, index) => index.toString(),
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getPaginationRowModel: getPaginationRowModel(),
    renderFallbackValue: '-',
  });

  if (settings.isError) {
    return <SomethingWentWrong error={settings.error} />;
  }

  return (
    <div className="flex flex-col gap-16 py-16">
      <div className="flex flex-wrap-reverse items-end justify-between gap-16">
        <div className="flex w-full flex-col gap-8 ls:w-auto">
          <div className="flex w-full items-center gap-8 ls:w-auto">
            <Input
              id="search"
              placeholder="Search"
              className="w-full ls:max-w-[300px]"
              defaultValue={search.filter}
              onChange={(e) => debouncedGlobalFilter(e.currentTarget.value)}
            />
          </div>

          <div className="rounded-sm px-8 py-4">
            <Typography
              variant="interface-sm"
              leadingNone
              uppercase
              className="opacity-70"
            >
              {table.getFilteredRowModel().flatRows.length} rows
            </Typography>
          </div>
        </div>

        <div className="flex w-full items-center justify-end gap-16 ls:w-auto">
          <AddDomains />
        </div>
      </div>

      <div ref={tableRef} className="w-full">
        <Table
          table={table}
          data-cy="domains-table"
          className="hidden w-full lm:table"
          loading={settings.isPending}
          pagination
        ></Table>
      </div>
    </div>
  );
};
